import { Box, ButtonBase, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { AgentStatusPillProps } from './AgentStatusPill.props';
import { ActiveContact } from '../../../@types/connect.type';
import { formatDuration } from '../../../utils/phone-format';
import { AgentStatusDot } from '../../atoms/AgentStatusDot';

const elapsedSeconds = (iso: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
};

const headlineForCall = (state: ActiveContact['state']): string => {
  if (state === 'connected') return 'On call';
  if (state === 'connecting') return 'Connecting…';
  return 'Ringing';
};

// Used both as a status display and as a click target to open the secondary
// drawer. Button semantics so screen readers announce it correctly.

export const AgentStatusPill: React.FC<AgentStatusPillProps> = ({ agent, contact, onClick }) => {
  const [, forceTick] = useState(0);

  // Tick once a second so the elapsed-time string stays current. Local state
  // bump avoids re-rendering parent components.
  useEffect(() => {
    const id = window.setInterval(() => forceTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Active call wins the headline — agent state alone is uninformative when
  // there's a live customer waiting.
  const isCalling = contact !== null && contact.state !== 'ended';
  const headline = isCalling ? headlineForCall(contact.state) : (agent?.status.name ?? 'Offline');
  const dotKind = isCalling ? 'onCall' : (agent?.status.kind ?? 'default');
  const elapsedFromIso = isCalling
    ? (contact.connectedAt ?? contact.startedAt)
    : (agent?.statusChangedAt ?? null);
  const elapsed = elapsedSeconds(elapsedFromIso);

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`Open phone panel — ${headline}`}
      sx={{
        width: '100%',
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: '#e2e8f0',
        textAlign: 'left',
        '&:hover': { bgcolor: 'rgba(148,163,184,0.12)' },
        '&:focus-visible': { outline: '2px solid #38bdf8', outlineOffset: 2 },
      }}
    >
      <AgentStatusDot kind={dotKind} pulse={isCalling} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.2 }}
        >
          {headline}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.2 }}>
          {elapsed !== null ? formatDuration(elapsed) : '—'}
        </Typography>
      </Box>
    </ButtonBase>
  );
};
